// Role configuration for the admin portal
// This file centralizes role definitions so they can be easily customized per page

export interface RoleConfig {
  value: string;
  label: string;
  description: string;
  group: 'client' | 'admin';
  color: string;
  priority: number; // Lower = higher priority in sorting
}

// All available roles with their configurations
export const allRoles: RoleConfig[] = [
  // Client App Users
  {
    value: 'owner',
    label: 'Owner',
    description: 'Studio owner - gets trial',
    group: 'client',
    color: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    priority: 5,
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Studio manager',
    group: 'client',
    color: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    priority: 6,
  },
  {
    value: 'therapist',
    label: 'Therapist',
    description: 'Massage therapist',
    group: 'client',
    color: 'bg-sage/10 text-sage',
    priority: 7,
  },
  // Admin Portal Users
  {
    value: 'pending',
    label: 'Pending',
    description: 'No access until approved',
    group: 'admin',
    color: 'bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400',
    priority: 4,
  },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access to both apps',
    group: 'admin',
    color: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    priority: 1,
  },
  {
    value: 'marketing',
    label: 'Marketing',
    description: 'Admin portal only',
    group: 'admin',
    color: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    priority: 2,
  },
  {
    value: 'sales',
    label: 'Sales',
    description: 'Admin portal only',
    group: 'admin',
    color: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400',
    priority: 3,
  },
];

// Helper to get roles by group
export const getClientRoles = (): RoleConfig[] =>
  allRoles.filter(r => r.group === 'client');

export const getAdminRoles = (): RoleConfig[] =>
  allRoles.filter(r => r.group === 'admin');

// Helper to get a role color by value
export const getRoleColor = (roleValue: string): string => {
  const role = allRoles.find(r => r.value === roleValue);
  return role?.color || 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400';
};

// Helper to get role priority for sorting
export const getRolePriority = (roleValue: string): number => {
  const role = allRoles.find(r => r.value === roleValue);
  return role?.priority || 99;
};

// Pre-defined role sets for different pages
export const rolePresets = {
  // For Users page - all roles that can be assigned via invite
  usersPageInvite: ['owner', 'pending', 'admin', 'marketing', 'sales'],

  // For role change dropdown - all roles users can be changed to
  usersPageRoleChange: ['owner', 'admin', 'marketing', 'sales', 'pending'],

  // For filtering users by role
  usersPageFilter: ['owner', 'manager', 'therapist', 'admin', 'marketing', 'sales', 'pending'],
};

// Get role configs for a specific preset
export const getRolesForPreset = (presetName: keyof typeof rolePresets): RoleConfig[] => {
  const roleValues = rolePresets[presetName];
  return roleValues
    .map(value => allRoles.find(r => r.value === value))
    .filter((r): r is RoleConfig => r !== undefined);
};

// Build role colors map (for backward compatibility)
export const roleColors: Record<string, string> = Object.fromEntries(
  allRoles.map(r => [r.value, r.color])
);

// Build role priority map (for backward compatibility)
export const rolePriority: Record<string, number> = Object.fromEntries(
  allRoles.map(r => [r.value, r.priority])
);
